import { useEffect, useState } from "react";
import {
  BadgeCheck, FileText, Globe2, Heart, Package, Plane, Search, ShieldCheck, ShoppingBag, Briefcase,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { DestinationCard, FinalCta, QuoteTrackButtons, SectionHead, StepsLine, usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";
import VideoHeroBg from "../components/service/VideoHeroBg";
import AiCta from "../components/ai/AiCta";

const WORLD_IMG = "https://static.prod-images.emergentagent.com/jobs/6e217b82-4821-4792-98ef-42642f657e6a/images/371456d4aca119de66c162c8a20677838d7294e7463d84486ed34eef7015251d.jpeg";

const NETWORK_STATS = [
  { v: "200+", l: "Countries & Territories" }, { v: "Express & Economy", l: "Service Options" },
  { v: "Door-to-Door", l: "Delivery" }, { v: "Live", l: "Shipment Tracking" },
];

const SERVICES = [
  { icon: FileText, title: "Documents", desc: "Important personal and business documents, delivered worldwide." },
  { icon: Package, title: "Personal Parcels", desc: "Personal belongings and gifts for family and friends abroad." },
  { icon: Heart, title: "NRI Shipments", desc: "Food, gifts and personal items where permitted by destination rules." },
  { icon: Briefcase, title: "Business Exports", desc: "International shipments for businesses with documentation support." },
  { icon: ShoppingBag, title: "eCommerce", desc: "Cross-border shipping for online businesses and sellers." },
  { icon: BadgeCheck, title: "DDP Shipping", desc: "Duty-paid shipping options where available." },
];

const COURIERS = ["DHL", "FedEx", "UPS", "DPD", "Aramex", "DTDC", "Blue Dart", "Delhivery", "Xpressbees"];

const JOURNEY = [
  { n: "01", title: "Booking" }, { n: "02", title: "Pickup" }, { n: "03", title: "Export Processing" },
  { n: "04", title: "Air Transit" }, { n: "05", title: "Customs" }, { n: "06", title: "Destination" }, { n: "07", title: "Delivery" },
];

const CUSTOMS_POINTS = [
  { icon: FileText, title: "Documentation", desc: "Commercial invoice and shipping paperwork guided by our team." },
  { icon: ShieldCheck, title: "Customs Declaration", desc: "Accurate declarations for smooth clearance." },
  { icon: Package, title: "Packaging", desc: "Export-grade packing for international transit." },
  { icon: Globe2, title: "Product Description", desc: "Clear item descriptions that customs authorities require." },
  { icon: BadgeCheck, title: "Duties & Taxes", desc: "Guidance on destination duties and DDP options where available." },
  { icon: Plane, title: "Destination Restrictions", desc: "We check what is permitted before your parcel leaves India." },
];

const DESTINATIONS = [
  { name: "USA", img: "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Canada", img: "https://images.unsplash.com/photo-1517935706615-2717063c2225?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "UK", img: "https://images.unsplash.com/photo-1486299267070-83823f5448dd?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Australia", img: "https://images.unsplash.com/photo-1549180030-48bf079fb38a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "New Zealand", img: "https://images.unsplash.com/photo-1531804226530-70f8004aa44e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Europe", img: "https://images.unsplash.com/photo-1570097703229-b195d6dd291f?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Middle East", img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Asia", img: "https://images.unsplash.com/photo-1620033263019-f2ec2c738a60?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
];

export default function InternationalCourier() {
  const [contact, setContact] = useState(null);
  usePageMeta(
    "International Courier From India to 200+ Countries | IndiaFast",
    "Reliable international courier solutions connecting India with 200+ countries — documents, parcels, NRI shipments, business exports and eCommerce with door-to-door tracking.",
    "/international-courier"
  );
  useEffect(() => { api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="international-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-24 pt-32 sm:pt-36" data-testid="international-hero">
        <VideoHeroBg mp4="/videos/air.mp4" webm="/videos/air.webm"
                     poster="https://static.prod-images.emergentagent.com/jobs/6e217b82-4821-4792-98ef-42642f657e6a/images/4d07a792ea5825d3e00feb87f3f43680643695117c992fbbb1a70f89ce65bf86.jpeg"
                     posterAlt="IndiaFast branded cargo aircraft being loaded" badge="Air Cargo" chip="AWB IFAST1000001 · Departed BOM" testid="intl-hero-video" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-saffron">International Courier</p>
            <h1 className="animate-fade-up stagger-1 mt-3 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              From India <span className="text-saffron">to the World</span>
            </h1>
            <p className="animate-fade-up stagger-2 mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Reliable international courier solutions connecting India with 200+ countries.
            </p>
            <div className="animate-fade-up stagger-3"><QuoteTrackButtons quoteLabel="Get International Quote" testid="international" /></div>
            <div className="animate-fade-up stagger-4 mt-4"><AiCta label="Ask about international shipping" action="start:rate_intl" testid="intl-ask-ai-btn" /></div>
          </div>
          <div className="relative hidden lg:block">
            <img src={WORLD_IMG} alt="IndiaFast international routes from India" className="w-full rounded-3xl border border-white/10 object-cover shadow-2xl" />
            <span className="network-pulse absolute left-[46%] top-[38%] h-3 w-3 rounded-full bg-saffron shadow-lg shadow-saffron/60" />
          </div>
        </div>
      </section>

      {/* GLOBAL NETWORK */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="intl-network">
        <SectionHead kicker="Global Network" title="Ship Across the World" />
        <div className="relative mt-10 overflow-hidden rounded-3xl border border-slate-200 bg-navy shadow-xl">
          <img src={WORLD_IMG} alt="World shipping route network from India" loading="lazy" className="h-64 w-full object-cover sm:h-[400px]" />
          <div className="absolute inset-x-0 bottom-0 grid grid-cols-2 gap-3 bg-gradient-to-t from-navy/95 via-navy/70 to-transparent p-5 pt-14 lg:grid-cols-4">
            {NETWORK_STATS.map((s, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur" data-testid={`intl-netstat-${i}`}>
                <p className="font-heading text-sm font-extrabold text-white sm:text-base">{s.v}</p>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-saffron">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="intl-services">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="International Services" title="Whatever You're Sending, There's a Service for It" />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <div key={i} data-testid={`intl-service-${i}`}
                   className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all hover:-translate-y-1 hover:border-saffron/40 hover:bg-white hover:shadow-xl">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><s.icon className="h-6 w-6" /></span>
                <h3 className="mt-4 font-heading text-lg font-bold text-navy">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURIER NETWORK */}
      <section className="border-y border-slate-200 bg-slate-50 py-12" data-testid="intl-couriers">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Trusted Courier Network</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {COURIERS.map((c, i) => (
              <span key={c} data-testid={`intl-courier-${i}`}
                    className="grid h-11 min-w-[92px] place-items-center rounded-xl border border-slate-200 bg-white px-4 font-heading text-sm font-extrabold uppercase tracking-wide text-navy shadow-sm">
                {c}
              </span>
            ))}
          </div>
          <p className="mt-5 text-center text-[11px] text-slate-400">Courier availability varies by destination, service type and shipment.</p>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid="intl-journey">
        <SectionHead kicker="International Shipping Journey" title="From Your Door in India to Any Door in the World" center />
        <StepsLine steps={JOURNEY} testid="intl-steps" cols="lg:grid-cols-7" />
      </section>

      {/* CUSTOMS */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="intl-customs">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-3xl">
            <img src="https://images.unsplash.com/photo-1583911026662-95161686d9a6?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                 alt="Airport cargo operations" loading="lazy" className="h-72 w-full object-cover sm:h-[440px]" />
            <span className="tricolor-beam absolute inset-x-0 bottom-0 h-1.5" />
          </div>
          <div>
            <SectionHead kicker="Customs & Documentation" title="International Shipping Made Easier" />
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              {CUSTOMS_POINTS.map((c, i) => (
                <div key={i} className="flex gap-3" data-testid={`intl-customs-${i}`}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-600/10 text-emerald-600"><c.icon className="h-4.5 w-4.5 h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-bold text-navy">{c.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="intl-destinations">
        <SectionHead kicker="Popular Destinations" title="Where Is Your Shipment Going?" />
        <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {DESTINATIONS.map((d) => (
            <DestinationCard key={d.name} name={d.name} img={d.img} testid={`intl-dest-${d.name.toLowerCase().replace(/\s+/g, "-")}`} />
          ))}
        </div>
      </section>

      <FinalCta testid="intl-final-cta"
                img="https://images.unsplash.com/photo-1774698078446-59299e016718?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
                title="Your World Is Just a Shipment Away" primaryLabel="Get International Quote" primaryTo="/rate-calculator" primaryTestid="intl-final-quote"
                secondary={<a href="/track" data-testid="intl-final-track" className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10"><Search className="h-4 w-4" /> Track Shipment</a>} />
      <Footer contact={contact} />
    </div>
  );
}
